// FC246/1279: pure, conservative receipt-scoped allocation helpers.
export const PENDING_TRAINER_ALLOCATION_KEY = "trainer_deferred_allocation_pending_v1";

export const parseAllocationCount = (value) => {
  const text = String(value ?? "").trim();
  if (!/^(0|[1-9][0-9]*)$/.test(text)) return null;
  const number = Number(text);
  return Number.isSafeInteger(number) && number <= 2147483647 ? number : null;
};

export const requestedPhysicalCards = (single, playset) => single + 4 * playset;

export const allocationDraftError = (singleInput, playsetInput, source) => {
  const singles = parseAllocationCount(singleInput);
  const playsets = parseAllocationCount(playsetInput);
  if (singles === null || playsets === null) return "Enter whole-number quantities of singles and playsets.";
  const physical = requestedPhysicalCards(singles, playsets);
  if (physical < 1 || physical > 2147483647) return "Allocate at least one card without exceeding the quantity limit.";
  if (!source || source.blocked_reason || source.remaining_quantity === null || source.remaining_quantity === undefined)
    return source?.blocked_reason || "Choose a confirmed, unallocated physical receipt.";
  if (physical > Number(source.remaining_quantity)) return "Requested cards exceed this receipt's remaining quantity.";
  return "";
};

export const makeDeferredAllocationPayload = (group, singles, playsets, requestId) => ({
  request_id: requestId,
  catalog_card_id: Number(group.catalog_card_id),
  name_and_number: group.name_and_number,
  set_name: group.set_name,
  card_variant: group.card_variant,
  single_allocation_quantity: singles,
  playset_allocation_quantity: playsets,
});

export const makeDeferredAllocationId = () => {
  const uuid = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `deferred-${uuid}`;
};

export const persistPendingTrainerAllocation = (pending) => {
  try {
    if (pending) window.localStorage.setItem(PENDING_TRAINER_ALLOCATION_KEY, JSON.stringify(pending));
    else window.localStorage.removeItem(PENDING_TRAINER_ALLOCATION_KEY);
    return true;
  } catch (_) { return false; }
};

export const readPendingTrainerAllocation = () => {
  try {
    const raw = window.localStorage.getItem(PENDING_TRAINER_ALLOCATION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Number.isSafeInteger(Number(data.rowId)) && Number(data.rowId) > 0 &&
      typeof data.payload?.request_id === "string" && data.payload.request_id.length >= 8 &&
      Number.isSafeInteger(data.payload.catalog_card_id) && data.payload.catalog_card_id > 0 &&
      Number.isSafeInteger(data.payload.single_allocation_quantity) &&
      Number.isSafeInteger(data.payload.playset_allocation_quantity) ? data : null;
  } catch (_) { return null; }
};

// Pagination may split identical prints across pages. Merge by exact catalogue ID
// plus finish, not by card name, and deduplicate source rows on refresh.
export const mergeAllocationGroups = (oldGroups, newGroups) => {
  const map = new Map();
  for (const group of [...oldGroups, ...newGroups]) {
    const key = `${group.catalog_card_id}::${group.card_variant}`;
    if (!map.has(key)) map.set(key, { ...group, sources: [] });
    const current = map.get(key);
    const seen = new Set(current.sources.map((source) => source.intake_row_id));
    for (const source of group.sources || []) {
      if (!seen.has(source.intake_row_id)) {
        current.sources.push(source);
        seen.add(source.intake_row_id);
      }
    }
    current.remaining_physical_quantity = current.sources.reduce(
      (n, source) => n + (Number(source.remaining_quantity) || 0), 0
    );
  }
  return [...map.values()];
};
